import * as Sentry from "@sentry/react";
import { hasAnalyticsConsent } from "@/lib/cookieConsent";
import { logger } from "@/lib/logger";

type AuthAction =
  | "sign_in"
  | "sign_up"
  | "sign_out"
  | "token_refresh"
  | "session_restore"
  | "mfa_verify"
  | "password_reset"
  | "sso_login";

/**
 * Expected auth error codes that represent normal user behavior,
 * not application errors. These are logged as debug info only.
 */
const EXPECTED_AUTH_ERRORS = new Set([
  "invalid_credentials",
  "invalid_login_credentials",
  "invalid_grant",
  "user_not_found",
  "email_not_confirmed",
]);

/**
 * Capture an auth error in Sentry with structured tags and context.
 * Only sends if the user has given analytics consent.
 * PII-safe: never logs full email addresses, only domains.
 *
 * Filters out expected user errors (wrong password, unconfirmed email)
 * to reduce Sentry noise. Only real app errors (500s, timeouts) are reported.
 */
export function captureAuthError(
  action: AuthAction,
  error: { message: string; status?: number; code?: string },
  extra?: Record<string, unknown>,
): void {
  if (!hasAnalyticsConsent()) return;

  // Skip expected user errors (e.g. wrong password) – these are not app bugs
  const errorCode = error.code?.toLowerCase() ?? "";
  const isExpectedUserError =
    EXPECTED_AUTH_ERRORS.has(errorCode) ||
    (error.status === 400 && error.message?.toLowerCase().includes("invalid login credentials"));

  if (isExpectedUserError) return;

  // Structured local log for the auth critical path. PII-safe: only action, error
  // code and status. We deliberately omit `error.message` (its free text could
  // carry an e-mail address) and the caller-supplied `extra`.
  //
  // Uses `logger.warn` (not `error`): this function already reports the single
  // intended Sentry event via `Sentry.captureMessage` below. `logger.error`
  // auto-reports to Sentry too, which would create a duplicate event. `warn` is
  // visible in the prod console (default prod level is `warn`) but never auto-Sentries.
  logger.warn("Auth failure", {
    action,
    code: error.code ?? "unknown",
    status: error.status,
  });

  Sentry.withScope((scope) => {
    scope.setTag("auth.action", action);
    scope.setTag("auth.error_code", error.code ?? "unknown");
    if (error.status) scope.setTag("auth.status", String(error.status));

    const level = action === "token_refresh" || action === "sign_out" ? "warning" : "error";
    scope.setLevel(level);
    scope.setContext("auth_error", { ...error, ...extra });

    Sentry.captureMessage(`Auth failure: ${action} – ${error.message}`, level);
  });
}
